// Pure step reducer — takes current visual state + one step, returns next visual state.
// The store calls this; it never imports from the store.
// Add new event types here without touching useDSAStore.

export function applyStep(state, step) {
  let elements    = state.elements.map((e) => ({ ...e }))
  let nodes       = state.nodes.map((n) => ({ ...n }))
  let edges       = state.edges.map((e) => ({ ...e }))
  let nodePointers = { ...state.nodePointers }
  let pointers    = { ...state.pointers }
  let variables   = { ...state.variables }
  let callStack   = state.callStack ? [...state.callStack] : []
  let gridRows    = state.gridRows ?? 0
  let gridCols    = state.gridCols ?? 0
  let gridCells   = state.gridCells ? [...state.gridCells] : []
  let dpRows      = state.dpRows ?? 0
  let dpCols      = state.dpCols ?? 0
  let dpCells     = state.dpCells ? state.dpCells.map((c) => ({ ...c })) : []
  let dpRowLabels = state.dpRowLabels ? [...state.dpRowLabels] : []
  let dpColLabels = state.dpColLabels ? [...state.dpColLabels] : []
  let btNodes     = state.btNodes ? [...state.btNodes] : []
  let btEdges     = state.btEdges ? [...state.btEdges] : []
  let operation   = state.operation
  let explanation = state.explanation

  if (step.operation   !== undefined) operation   = step.operation
  if (step.explanation !== undefined) explanation = step.explanation

  switch (step.type) {
    // ── Array / List ──────────────────────────────────────────────────────────
    case 'init':
      elements = step.elements.map((v, i) => ({ id: i, value: v, state: 'inactive', meta: {} }))
      pointers  = {}
      variables = {}
      break

    case 'set-state':
      step.indices.forEach((i) => {
        if (elements[i]) elements[i] = { ...elements[i], state: step.state }
      })
      break

    case 'clear-state': {
      const from = step.clearStates || ['active', 'compared']
      elements = elements.map((el) => from.includes(el.state) ? { ...el, state: 'inactive' } : el)
      break
    }

    case 'swap': {
      const [a, b] = step.indices
      const va = elements[a]?.value
      const vb = elements[b]?.value
      if (elements[a]) elements[a] = { ...elements[a], value: vb }
      if (elements[b]) elements[b] = { ...elements[b], value: va }
      break
    }

    case 'pointer':
      if (step.remove) {
        const next = { ...pointers }
        delete next[step.name]
        pointers = next
      } else {
        pointers = { ...pointers, [step.name]: step.index }
      }
      break

    case 'vars':
      variables = { ...variables, ...step.vars }
      break

    case 'delete-vars': {
      const next = { ...variables }
      step.keys.forEach((k) => delete next[k])
      variables = next
      break
    }

    case 'replace-elements':
      elements = step.elements.map((v, i) => ({ id: i, value: v, state: 'inactive', meta: {} }))
      break

    // ── Tree / Graph ──────────────────────────────────────────────────────────
    case 'init-graph':
      nodes       = step.nodes.map((n) => ({ ...n, state: n.state || 'inactive' }))
      edges       = (step.edges || []).map((e) => ({ ...e, state: e.state || 'inactive' }))
      nodePointers = {}
      pointers    = {}
      variables   = {}
      callStack   = []
      break

    case 'set-node-state':
      step.ids.forEach((id) => {
        const idx = nodes.findIndex((n) => n.id === id)
        if (idx !== -1) nodes[idx] = { ...nodes[idx], state: step.state }
      })
      break

    case 'clear-node-state': {
      const from = step.clearStates || ['active', 'compared']
      nodes = nodes.map((n) => from.includes(n.state) ? { ...n, state: 'inactive' } : n)
      break
    }

    case 'set-edge-state':
      step.edges.forEach(([from, to]) => {
        const idx = edges.findIndex(
          (e) => (e.from === from && e.to === to) || (e.from === to && e.to === from)
        )
        if (idx !== -1) edges[idx] = { ...edges[idx], state: step.state }
      })
      break

    case 'set-node-value': {
      const idx = nodes.findIndex((n) => n.id === step.id)
      if (idx !== -1) nodes[idx] = { ...nodes[idx], value: step.value }
      break
    }

    case 'move-nodes':
      step.positions.forEach(({ id, x, y }) => {
        const idx = nodes.findIndex((n) => n.id === id)
        if (idx !== -1) nodes[idx] = { ...nodes[idx], x, y }
      })
      break

    case 'node-pointer':
      if (step.remove) {
        const next = { ...nodePointers }
        delete next[step.name]
        nodePointers = next
      } else {
        nodePointers = { ...nodePointers, [step.name]: step.id }
      }
      break

    // ── Recursion call stack ──────────────────────────────────────────────────
    case 'recurse-enter':
      callStack = [step.label, ...callStack]
      if (step.nodeId != null) {
        const idx = nodes.findIndex((n) => n.id === step.nodeId)
        if (idx !== -1) nodes[idx] = { ...nodes[idx], state: 'current-frame' }
      }
      break

    case 'recurse-exit':
      callStack = callStack.slice(1)
      break

    // ── Grid ─────────────────────────────────────────────────────────────────
    case 'init-grid':
      gridRows  = step.rows
      gridCols  = step.cols
      gridCells = step.cells.flat().map((c) => ({
        value: c?.value ?? '',
        state: c?.state ?? 'inactive',
      }))
      pointers  = {}
      variables = {}
      break

    case 'set-cell-state':
      step.cells.forEach(({ row, col, state: s, value }) => {
        const i = row * gridCols + col
        if (i >= 0 && i < gridCells.length) {
          gridCells[i] = { ...gridCells[i], state: s }
          if (value !== undefined) gridCells[i].value = value
        }
      })
      break

    // ── DP Table ──────────────────────────────────────────────────────────────
    case 'init-dp-table':
      dpRows      = step.rows
      dpCols      = step.cols
      dpCells     = Array.from({ length: step.rows * step.cols }, () => ({ value: '', state: 'inactive' }))
      dpRowLabels = step.rowLabels || []
      dpColLabels = step.colLabels || []
      pointers    = {}
      variables   = {}
      break

    case 'set-dp-cell': {
      const i = step.row * dpCols + step.col
      if (i >= 0 && i < dpCells.length) {
        dpCells[i] = { value: step.value, state: step.state || 'active' }
      }
      break
    }

    // ── Backtracking tree ─────────────────────────────────────────────────────
    case 'bt-init':
      btNodes = [{ id: 'root', label: step.label || 'start', state: 'active', x: 0, y: 0 }]
      btEdges = []
      variables = {}
      break

    case 'branch-enter': {
      const parentNode = btNodes.find((n) => n.id === step.parentId)
      const siblings   = btEdges.filter((e) => e.from === step.parentId).length
      const newX = parentNode ? parentNode.x + (siblings - 1) * 1.8 : 0
      const newY = parentNode ? parentNode.y - 2.0 : 0
      btNodes = [...btNodes, { id: step.nodeId, label: String(step.choice), state: 'active', x: newX, y: newY }]
      if (step.parentId) btEdges = [...btEdges, { from: step.parentId, to: step.nodeId, state: 'active' }]
      variables = { ...variables, path: `[${(step.path || []).join(', ')}]` }
      break
    }

    case 'branch-exit': {
      const idx = btNodes.findIndex((n) => n.id === step.nodeId)
      if (idx !== -1) btNodes[idx] = { ...btNodes[idx], state: 'visited' }
      break
    }

    case 'branch-prune': {
      const idx = btNodes.findIndex((n) => n.id === step.nodeId)
      if (idx !== -1) btNodes[idx] = { ...btNodes[idx], state: 'pruned' }
      const eIdx = btEdges.findIndex((e) => e.to === step.nodeId)
      if (eIdx !== -1) btEdges[eIdx] = { ...btEdges[eIdx], state: 'rejected' }
      break
    }

    case 'solution-found':
      step.nodeIds.forEach((id) => {
        const idx = btNodes.findIndex((n) => n.id === id)
        if (idx !== -1) btNodes[idx] = { ...btNodes[idx], state: 'answer' }
      })
      step.nodeIds.forEach((id, k) => {
        if (k === 0) return
        const eIdx = btEdges.findIndex((e) => e.to === id && step.nodeIds.includes(e.from))
        if (eIdx !== -1) btEdges[eIdx] = { ...btEdges[eIdx], state: 'answer' }
      })
      break

    default:
      break
  }

  return {
    elements, nodes, edges, nodePointers, pointers, variables,
    operation, explanation, callStack,
    gridRows, gridCols, gridCells,
    dpRows, dpCols, dpCells, dpRowLabels, dpColLabels,
    btNodes, btEdges,
  }
}
